import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useQueries, UseQueryOptions } from 'react-query';
import { AppointmentTypesApi } from '@frontend/api-appointment-types';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { ScheduleTypes } from '@frontend/api-schedule';
import { ExceptionQueries } from '@frontend/api-schedule-exceptions';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import {
  ServiceProvidersApi,
  ServiceProvidersConstants,
  ServiceProvidersQueries,
  ServiceProvidersQueryKeys,
} from '@frontend/api-service-providers';
import { getTodaysDate } from '@frontend/date';
import { InsuranceTabHooks } from '@frontend/insurance-tab';
import { getFullName } from '@frontend/name';
import { useQuery } from '@frontend/react-query-helpers';
import { SettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { useLocation } from '@frontend/weave-router';
import type { FilterableCheckboxListItem } from '../components/filterable-checkbox-list/use-filterable-checkbox-list';
import { MAX_CALENDAR_LOCATION_COUNT } from '../constants';
import { useCalendarLocationIds } from '../stores/calendar-location.store';
import { useCalendarStore } from '../stores/calendar.store';
import { getOneDayRangeFromNow } from '../utils';
import { getCombinedAppointmentTypes } from '../utils/get-combined-appointment-types';
import {
  getFilteredAppointments,
  getInsuranceStatusesFilterList,
  getUniqueOperatoryList,
} from '../views/Calendar/helpers';
import { APPT_STATUS_ALL_OPTION, useGetAppointmentStatusList } from './use-get-appointment-status-list';
import { useGetCombinedProvidersList } from './use-get-combined-providers-list';
import { useGetOfficeHoursForMultipleLocationIds } from './use-get-office-hours';
import { useGetOperatoryList } from './use-get-operatory-list';
import { useGetProvidersListForMultipleLocationIds } from './use-get-providers-list';
import { useGetProvidersOfficeHoursList } from './use-get-providers-office-hours-list';
import { useSchedulingLocationInfo } from './use-scheduling-location-info';

dayjs.extend(utc);

const SCHEDULE_SETTINGS_ROUTES: Record<'providers' | 'appointmentHours' | 'appointmentTypes', SettingsRoutes> = {
  providers: '/schedule/manage-providers',
  appointmentHours: '/schedule/appointment-hours',
  appointmentTypes: '/schedule/appointment-types',
};

export const useGetCalendarV2QueriesData = () => {
  const current = useLocation();
  const { getScopeName, selectedLocationIds } = useSchedulingLocationInfo();
  const { isSchedulerV2FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const {
    calendarDate,
    calendarOperatories,
    calendarApptStatuses,
    calendarApptTypeIds,
    calendarProviderIds,
    isForSchedulePulse,
  } = useCalendarStore([
    'calendarDate',
    'calendarOperatories',
    'calendarApptStatuses',
    'calendarApptTypeIds',
    'calendarProviderIds',
    'isForSchedulePulse',
  ]);
  const { isOpen: isSettingModalOpen } = useSettingsNavigate();
  const { calendarLocationIds: _calendarLocationIds } = useCalendarLocationIds();
  const locationIds = isForSchedulePulse ? selectedLocationIds : _calendarLocationIds;

  const [startDate, endDate] = useMemo(() => {
    if (!calendarDate) return ['', ''];

    const startDateInUTC = dayjs(String(calendarDate)).utc().toISOString();
    const endDateInUTC = dayjs(String(calendarDate)).add(1, 'day').add(-1, 's').utc().toISOString();
    return [startDateInUTC, endDateInUTC];
  }, [calendarDate]);

  //#region Queries
  const appointmentStatusListData = useGetAppointmentStatusList({ locationIds: locationIds });
  const operatoriesData = useGetOperatoryList(locationIds);

  const appointmentTypesQueries = useQueries(
    locationIds.map(
      (locationId): UseQueryOptions => ({
        queryKey: ['appointmentTypes', locationId],
        queryFn: () => AppointmentTypesApi.getAppointmentTypes(locationId),
        enabled: !!locationId && isSchedulerV2FlagEnabled,
      })
    )
  );

  const appointmentTypesData = appointmentTypesQueries.reduce(
    (acc, query) => {
      acc.isLoading = acc.isLoading || query.isLoading;

      acc.data.push(...((query.data ?? []) as Awaited<ReturnType<typeof AppointmentTypesApi.getAppointmentTypes>>));
      return acc;
    },
    { isLoading: false, data: [] as Awaited<ReturnType<typeof AppointmentTypesApi.getAppointmentTypes>> }
  );

  const providersData = useGetProvidersListForMultipleLocationIds({ locationIds: locationIds });
  const providerIds = useMemo(() => {
    return (providersData.data ?? []).map((provider) => provider.id ?? '').filter((id) => id) ?? [];
  }, [providersData.data]);

  const officeHoursData = useGetOfficeHoursForMultipleLocationIds({ locationIds: locationIds });
  const providersOfficeHoursData = useGetProvidersOfficeHoursList(calendarDate, providerIds ?? [], locationIds);

  const providerExceptionsData = ServiceProvidersQueries.useGetProvidersExceptionsForMultipleProvidersOfMultiLocations(
    {
      providerIds: providerIds,
      locationIds: locationIds,
      startDateTime: startDate,
      endDateTime: endDate,
      isAvailable: false,
    },
    isSchedulerV2FlagEnabled
  );
  const officeExceptionsData = ExceptionQueries.useGetOfficeExceptions(
    {
      locationIds: locationIds,
      startDateTime: startDate,
      endDateTime: endDate,
      isAvailable: false,
    },
    isSchedulerV2FlagEnabled
  );

  const { appointmentsQuery } = useGetFilteredAppointmentsV2(locationIds);

  //#endregion

  const isLoading =
    providersData.isLoading ||
    operatoriesData.isLoading ||
    officeHoursData.isLoading ||
    providersOfficeHoursData.isProvidersOfficeHoursLoading ||
    providerExceptionsData.isLoading ||
    officeExceptionsData.isLoading ||
    appointmentsQuery.isLoading;

  // useEffect to refetch provider if changes are made in the global settings for providers
  useEffect(() => {
    if (isSettingModalOpen) {
      return;
    }

    if (current.hash.includes(SCHEDULE_SETTINGS_ROUTES.providers)) {
      providersData.refetch();
      providerExceptionsData.refetch();
    }

    if (current.hash.includes(SCHEDULE_SETTINGS_ROUTES.appointmentHours)) {
      officeHoursData.refetch();
    }

    if (current.hash.includes(SCHEDULE_SETTINGS_ROUTES.appointmentTypes)) {
      appointmentTypesQueries.forEach((query) => query.refetch());
    }
  }, [
    isSettingModalOpen,
    current.hash,
    selectedLocationIds,
    providersData,
    providerExceptionsData,
    officeHoursData,
    appointmentTypesQueries,
  ]);

  //#region Filter Lists

  const appointmentStatuses = useMemo<FilterableCheckboxListItem[]>(() => {
    return appointmentStatusListData
      .filter((item) => item !== APPT_STATUS_ALL_OPTION)
      .map((item) => ({
        label: item,
        value: item,
      }));
  }, [appointmentStatusListData]);
  const { combinedAppointmentTypes } = getCombinedAppointmentTypes(
    locationIds,
    appointmentTypesData.data,
    appointmentsQuery.data?.appointments ?? []
  );
  const appointmentTypes: FilterableCheckboxListItem[] = useMemo(() => {
    return combinedAppointmentTypes.map((item) => ({
      label: item.name,
      value: item.name, // NOTE: in V2 we need to use name as value as we were using values appointments as well
      locationName: locationIds.length > 1 ? getScopeName(item.locationId) : '',
    }));
  }, [combinedAppointmentTypes, locationIds.length, getScopeName]);

  const combinedProvidersList = useGetCombinedProvidersList(
    appointmentsQuery.data ?? {},
    (providersData.data ?? []) as ScheduleTypes.Provider[]
  );
  const providers = useMemo(() => {
    return combinedProvidersList
      .map<FilterableCheckboxListItem>((provider) => ({
        label: `${provider.publicDisplayName || getFullName(provider) || provider.resourceName || ''}`.trim(),
        value: provider.id || '',
        locationName: locationIds.length > 1 ? getScopeName(provider.locationId) : '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [locationIds.length, getScopeName, combinedProvidersList]);
  const operatories = useMemo(() => {
    const uniqueOperatories = getUniqueOperatoryList(locationIds, operatoriesData.operatories);
    return uniqueOperatories
      .map<FilterableCheckboxListItem>((operatory) => ({
        label: operatory.display_name || '',
        value: operatory.value || '',
        locationName: locationIds.length > 1 ? getScopeName(operatory.locationId) : '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [operatoriesData.operatories, locationIds, getScopeName]);

  const insuranceStatuses = useMemo(() => Array.from(getInsuranceStatusesFilterList().values()), []);

  //#endregion

  const filteredProviderList = combinedProvidersList.filter((p) => calendarProviderIds.includes(p.id));
  const filteredAppointments = getFilteredAppointments({
    appointmentsToFilter: appointmentsQuery.data?.appointments || [],
    providerList: filteredProviderList,
    selectedAppointmentTypes: calendarApptTypeIds,
    appointmentStatuses: calendarApptStatuses,
    selectedOperatoryList: calendarOperatories,
  });

  const { calendarInsuranceCount } = InsuranceTabHooks.useGetInsuranceStatuses({
    appointments: appointmentsQuery.data?.appointments || [],
    scheduleSelectedLocationIds: locationIds,
    isCalendarView: true,
    calendarDate: calendarDate,
  });

  const unconfirmedAppointmentsCount = useMemo(() => {
    return filteredAppointments.filter((appt) => appt.statusOfficeView?.toLowerCase() === 'unconfirmed').length;
  }, [filteredAppointments]);

  return {
    calendarLocationIds: locationIds,
    calendarTodayDate: getTodaysDate('MM/DD/YYYY'),
    isLoading,

    appointments: appointmentsQuery.data?.appointments || [],
    filteredAppointments,
    refetchAppointments: appointmentsQuery.refetch,

    providersOfficeHours: providersOfficeHoursData.providersOfficeHoursListData,
    providerExceptions: providerExceptionsData.data?.data ?? [],
    officeHours: officeHoursData.data ?? [],
    officeExceptions: officeExceptionsData.data?.data ?? [],
    refetchOfficeExceptions: officeExceptionsData.refetch,
    refetchProvidersExceptions: providerExceptionsData.refetch,

    providerList: combinedProvidersList,
    filteredProviderList: filteredProviderList.length > 0 ? filteredProviderList : combinedProvidersList,
    appointmentTypeList: combinedAppointmentTypes,

    calendarCounts: {
      unconfirmed: unconfirmedAppointmentsCount,
      total: filteredAppointments.length,
      insurance: calendarInsuranceCount,
    },
    filterLists: {
      appointmentStatuses,
      appointmentTypes,
      providers,
      operatories,
      insuranceStatuses,
    },
  };
};

export const useGetFilteredAppointmentsV2 = (locationIds: string[]) => {
  const { isScheduleV3FlagEnabled, isLoading: isLoadingFF } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');

  const {
    calendarDate,
    calendarProviderIds,
    calendarApptTypeIds,
    calendarApptStatuses,
    calendarInsuranceStatuses,
    calendarOperatories,
  } = useCalendarStore([
    'calendarDate',
    'calendarProviderIds',
    'calendarApptTypeIds',
    'calendarApptStatuses',
    'calendarInsuranceStatuses',
    'calendarOperatories',
  ]);

  const [startDate, endDate] = useMemo(() => getOneDayRangeFromNow(calendarDate), [calendarDate]);

  const providersData = useGetProvidersListForMultipleLocationIds({ locationIds: locationIds });

  const appointmentsQuery = useQuery({
    queryKey: ServiceProvidersQueryKeys.queryKeys.appointments({
      equals: {
        locationIds: locationIds,
        insuranceStatuses: calendarInsuranceStatuses,
      },
      between: { start: startDate, end: endDate },
    }),
    queryFn: () =>
      ServiceProvidersApi.listAppointments({
        between: { start: startDate, end: endDate },
        equals: {
          locationIds: locationIds,
          insuranceStatuses: calendarInsuranceStatuses,
        },
      }),
    retry: 1,
    cacheTime: 10 * ServiceProvidersConstants.ONE_MINUTE_IN_MILLISECONDS, // 10 mins
    staleTime: 10 * ServiceProvidersConstants.ONE_MINUTE_IN_MILLISECONDS, // 10 mins
    enabled:
      !!locationIds.length &&
      !!startDate &&
      !!endDate &&
      locationIds?.length <= MAX_CALENDAR_LOCATION_COUNT &&
      !isLoadingFF &&
      !isScheduleV3FlagEnabled,
    select: (data) => {
      if (demoSourceIds.length === 0) {
        return data;
      } else {
        return {
          ...data,
          appointments: data.appointments?.filter((appointment) => {
            if (!appointment.createdBySourceId) return true;
            return demoSourceIds.includes(appointment.createdBySourceId);
          }),
        };
      }
    },
    onSettled: () => SchedulerV3.trackScheduleAPIVersionChange('v2', { endpoint: 'listAppointments' }),
  });

  const filteredAppointments = getFilteredAppointments({
    appointmentsToFilter: appointmentsQuery.data?.appointments || [],
    providerList: (providersData.data ?? []).filter((p) => calendarProviderIds.includes(p.id ?? '')),
    selectedAppointmentTypes: calendarApptTypeIds,
    appointmentStatuses: calendarApptStatuses,
    selectedOperatoryList: calendarOperatories,
  });

  return { appointmentsQuery, filteredAppointments, isLoading: appointmentsQuery.isLoading };
};
